import { Composition, Folder, Still } from "remotion";
import {
  FACEBOOK_BANNER,
  FacebookBanner,
} from "./banners/facebook/FacebookBanner";
import { B2BVideo, calculateB2BMetadata } from "./b2b/B2BVideo";
import { DEFAULT_MUSIC } from "./b2b/music";
import { B2B_SIZES } from "./b2b/stage";
import { B2B_FPS, timingFor } from "./b2b/timing";
import { B2CVideo } from "./b2c/B2CVideo";
import { B2C_SIZES } from "./b2c/stage";
import {
  B2B_VARIANTS,
  B2C_VARIANTS,
  b2bId,
  b2cId,
  LANGS,
  MERCHANT_CUTS,
  MERCHANT_VARIANTS,
  merchantId,
} from "./catalog";
import { REELS } from "./reels/catalog";
import { REEL_COMPONENTS } from "./reels/components";
import { ReelVideo, reelTimeline } from "./reels/ReelVideo";
import { REEL } from "./reels/ui/ReelFrame";
import { B2C_DURATION, B2C_FPS } from "./b2c/timing";
import {
  calculateMerchantMetadata,
  MerchantVideo,
  merchantSchema,
} from "./merchant/MerchantVideo";
import { MERCHANT_SIZES } from "./merchant/stage";
import { MERCHANT_FPS, timelineFor } from "./merchant/timing";

/**
 * The B2C / B2B / Merchant lists (ids, languages, formats, cuts) live in
 * `catalog.ts`, the reels in `reels/catalog.ts`, both shared with the
 * render pipeline (`server/lib/targets.ts`).
 */
/** Studio preview brand; any live slug works (dropdown in the props panel). */
const MERCHANT_PREVIEW = "comblee";

export const RemotionRoot: React.FC = () => {
  return (
    <>
      {/* B2C landing video (ambassador page). One composition per language
          and format; the timeline is shared. `B2C-FR` / `B2C-EN` (square)
          is the landing's "3 steps" embed and the validated master. */}
      <Folder name="B2C">
        {LANGS.map((lang) =>
          B2C_VARIANTS.map(({ suffix, format }) => (
            <Composition
              key={`${lang}${suffix}`}
              id={b2cId(lang, suffix)}
              component={B2CVideo}
              durationInFrames={B2C_DURATION}
              fps={B2C_FPS}
              width={B2C_SIZES[format].width}
              height={B2C_SIZES[format].height}
              defaultProps={{ lang, format }}
            />
          )),
        )}
      </Folder>

      {/* B2B partner video (/partner page + LinkedIn). `B2B-FR` / `B2B-EN`
          (landscape) is the partner page embed and the master; `-Feed`
          (4:5) is the LinkedIn mobile feed cut. Soundtrack = `music` prop
          (b2b/music.ts); render another with --props='{"music":"…"}'. */}
      <Folder name="B2B">
        {LANGS.map((lang) =>
          B2B_VARIANTS.map(({ suffix, format }) => (
            <Composition
              key={`${lang}${suffix}`}
              id={b2bId(lang, suffix)}
              component={B2BVideo}
              durationInFrames={timingFor(DEFAULT_MUSIC).duration}
              calculateMetadata={calculateB2BMetadata}
              fps={B2B_FPS}
              width={B2B_SIZES[format].width}
              height={B2B_SIZES[format].height}
              defaultProps={{ lang, music: DEFAULT_MUSIC }}
            />
          )),
        )}
      </Folder>

      {/* Per-merchant B2C commercial (src/merchant). One template for every
          live brand: pick the brand with the `slug` prop (Studio dropdown,
          or --props on the CLI; scripts/render-merchants.ts batches them).
          Story (9:16) is the master; Feed 4:5 also fits the landing's
          /brands/<slug> hero. `MerchantBumper` = the ~6s cut. */}
      <Folder name="Merchant">
        {MERCHANT_CUTS.map(({ id, cut }) =>
          LANGS.map((lang) =>
            MERCHANT_VARIANTS.map(({ suffix, format }) => (
              <Composition
                key={`${id}-${lang}-${suffix}`}
                id={merchantId(id, lang, suffix)}
                component={MerchantVideo}
                schema={merchantSchema}
                durationInFrames={timelineFor(cut).duration}
                calculateMetadata={calculateMerchantMetadata}
                fps={MERCHANT_FPS}
                width={MERCHANT_SIZES[format].width}
                height={MERCHANT_SIZES[format].height}
                defaultProps={{
                  slug: MERCHANT_PREVIEW,
                  lang,
                  cut,
                  music: true,
                  sfx: true,
                }}
              />
            )),
          ),
        )}
      </Folder>

      {/* Instagram reels for Frak's account (src/reels): silent 9:16, all
          listed in reels/catalog.ts (the video server renders the same
          list). Cuts of B2C scenes, or reels of their own. */}
      <Folder name="Reels">
        {REELS.map((reel) =>
          "scenes" in reel ? (
            <Composition
              key={reel.id}
              id={reel.id}
              component={ReelVideo}
              durationInFrames={reelTimeline(reel.scenes).duration}
              fps={B2C_FPS}
              width={B2C_SIZES.portrait.width}
              height={B2C_SIZES.portrait.height}
              defaultProps={{ lang: reel.lang, scenes: reel.scenes }}
            />
          ) : (
            <Composition
              key={reel.id}
              id={reel.id}
              {...REEL_COMPONENTS[reel.reel]}
              width={REEL.width}
              height={REEL.height}
            />
          ),
        )}
      </Folder>

      {/* Still images for Frak's social pages (src/banners): not videos,
          not in any catalog, so the video server never renders them. */}
      <Folder name="Banners">
        <Still
          id="Banner-FR-Facebook"
          component={FacebookBanner}
          width={FACEBOOK_BANNER.width}
          height={FACEBOOK_BANNER.height}
        />
      </Folder>
    </>
  );
};
